import { useRef, useState } from 'react'
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaPinterestP,
  FaSkype,
  FaSnapchat,
  FaTelegram,
  FaTiktok,
  FaWhatsapp,
  FaXTwitter,
  FaYoutube,
} from 'react-icons/fa6'
import { FiPhone, FiMail, FiMapPin, FiArrowRight } from 'react-icons/fi'
import './Footer.css'
import { LEGAL_LINKS, OFFERING_LINKS, SYSTEM_LINKS } from './footerData'
import { CARD_ICONS } from './CardIcons'
import { useGeneralSettings } from './useGeneralSettings'
import { appUrl } from '@astro/config'
import { api } from '@astro/api'


const FALLBACK_TAGLINE =
  'Empowering lives through ancient Vedic wisdom. Authentic astrology with practical remedies for modern living.'

/** Non-empty trimmed string, else '' — the API sends empty strings for unset fields. */
const val = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '')

/** tel: wants digits; bare 10-digit Indian numbers get the 91 prefix. */
const telDigits = (raw) => {
  const digits = raw.replace(/\D/g, '')
  return digits.length === 10 ? `91${digits}` : digits
}

/** Pretty-print Indian numbers as +91 XXXXX XXXXX; anything else shows as sent. */
const displayPhone = (raw) => {
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 10) return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`
  if (digits.length === 12 && digits.startsWith('91')) return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`
  return raw
}

/** The admin saves bare domains ("www.x.com") — without a protocol the browser
 * would treat them as paths relative to our site. */
const toHref = (raw) => (/^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw.replace(/^\/+/, '')}`)

const looksLikeUrl = (raw) => /^(https?:\/\/|www\.)/i.test(raw) || raw.includes('/') || raw.includes('.')

/** Every social channel the admin panel can configure, in display order.
 * `href` covers platforms whose field may hold a number/handle instead of a URL. */
const SOCIAL_PLATFORMS = [
  { key: 'facebook', label: 'Facebook', Icon: FaFacebookF },
  { key: 'instagram', label: 'Instagram', Icon: FaInstagram },
  { key: 'twitter', label: 'X (Twitter)', Icon: FaXTwitter },
  { key: 'youtube', label: 'YouTube', Icon: FaYoutube },
  { key: 'linkedin', label: 'LinkedIn', Icon: FaLinkedinIn },
  { key: 'whatsapp', label: 'WhatsApp', Icon: FaWhatsapp, href: (v) => (looksLikeUrl(v) ? toHref(v) : `https://wa.me/${telDigits(v)}`) },
  { key: 'telegram', label: 'Telegram', Icon: FaTelegram, href: (v) => (looksLikeUrl(v) ? toHref(v) : `https://t.me/${v.replace(/^@/, '')}`) },
  { key: 'tiktok', label: 'TikTok', Icon: FaTiktok },
  { key: 'snapchat', label: 'Snapchat', Icon: FaSnapchat },
  { key: 'pinterest', label: 'Pinterest', Icon: FaPinterestP },
  { key: 'skype', label: 'Skype', Icon: FaSkype, href: (v) => (looksLikeUrl(v) ? toHref(v) : `skype:${v}?chat`) },
]

export default function Footer() {
  const { settings } = useGeneralSettings()

  // API details with the footer's previous copy as per-field fallback.
  const logo = val(settings.company_logo)
  const companyName = val(settings.company_name) || 'Astro Raju Maharaj Ji'
  const tagline = val(settings.footer_text) || FALLBACK_TAGLINE
  const phone = val(settings.phone) || val(settings.support_phone) || '+91 98765 43210'
  const contactEmail = val(settings.email) || val(settings.support_email) || 'info@astrorajumaharaj.com'
  const address = val(settings.address) || 'New Delhi, India'

  // Socials from the API: direct platform fields plus social_links, which the
  // backend sends as an object keyed by platform ({ twitter: "www.x.com" });
  // older array shapes are kept for safety. "x" is folded into "twitter".
  const socials = {}
  const addSocial = (platform, url) => {
    const key = platform === 'x' ? 'twitter' : platform
    const clean = val(url)
    if (key && clean && !socials[key]) socials[key] = clean
  }
  SOCIAL_PLATFORMS.forEach(({ key }) => addSocial(key, settings[key]))
  const rawSocialLinks = settings.social_links
  if (Array.isArray(rawSocialLinks)) {
    rawSocialLinks.forEach((entry) => {
      if (entry && typeof entry === 'object') {
        addSocial(String(entry.platform || entry.name || entry.label || '').toLowerCase(), entry.url || entry.link)
      }
    })
  } else if (rawSocialLinks && typeof rawSocialLinks === 'object') {
    Object.entries(rawSocialLinks).forEach(([platform, url]) => addSocial(platform.toLowerCase(), url))
  }
  // WhatsApp chat is offered project-wide even when the dedicated whatsapp
  // field is unset — fall back to the main phone (addSocial keeps the
  // dedicated number when it exists).
  addSocial('whatsapp', settings.phone)
  const socialIcons = SOCIAL_PLATFORMS.filter((p) => socials[p.key]).map((p) => ({
    ...p,
    url: p.href ? p.href(socials[p.key]) : toHref(socials[p.key]),
  }))

  const [logoFailed, setLogoFailed] = useState(false)
  const [email, setEmail] = useState('')
  const [subscribing, setSubscribing] = useState(false)
  const [toast, setToast] = useState(null)
  const toastTimer = useRef(null)

  const showToast = (message) => {
    window.clearTimeout(toastTimer.current)
    setToast(message)
    toastTimer.current = window.setTimeout(() => setToast(null), 3000)
  }

  const handleSubscribe = async (e) => {
    e.preventDefault()
    const cleanEmail = email.trim()
    if (!cleanEmail || subscribing) return
    setSubscribing(true)
    try {
      const res = await api.newsletter.subscribe({ email: cleanEmail })
      showToast(res?.message || 'Subscribed! Check your inbox for cosmic updates.')
      setEmail('')
    } catch (err) {
      showToast(err?.message || 'Could not subscribe right now. Please try again.')
    } finally {
      setSubscribing(false)
    }
  }

  return (
    <footer id="site-footer" className="ft-top-rule">

      <div style={{ position: 'relative', zIndex: 1, maxWidth: '1400px', margin: '0 auto', padding: '64px 24px 0' }}>
        <div className="ft-grid">
          <div>
            {/* Same brand treatment as the app navbars: the admin's
                company_logo on a white pill (the footer is dark, and the
                logo's own ink may be dark too), falling back to the ॐ
                roundel + company name while settings load, when no logo is
                configured, or if the image URL fails to load. */}
            <a
              href={appUrl('website')}
              style={{ display: 'inline-block', marginBottom: '20px' }}
            >
              {logo && !logoFailed ? (
                <img
                  className="ft-logo-img"
                  src={logo}
                  alt={companyName}
                  loading="lazy"
                  onError={() => setLogoFailed(true)}
                />
              ) : (
                <span className="ft-brand-fallback">
                  <span className="ft-brand-om">ॐ</span>
                  <span className="ft-brand-text">
                    <span className="ft-brand-name">{companyName}</span>
                    <span className="ft-brand-sub">Vedic Wisdom Master</span>
                  </span>
                </span>
              )}
            </a>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
              <span style={{ height: '1px', width: '24px', background: 'rgba(245,158,11,0.35)', display: 'block' }} />
              <span style={{ fontSize: '14px', color: '#F59E0B', opacity: 0.55, lineHeight: 1 }}>ॐ</span>
              <span style={{ height: '1px', width: '24px', background: 'rgba(245,158,11,0.35)', display: 'block' }} />
            </div>
            <p className="ft-tagline">{tagline}</p>
            {socialIcons.length > 0 && (
              <div className="ft-socials">
                {socialIcons.map(({ key, label, Icon, url }) => (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="ft-social" aria-label={label} title={label}>
                    <Icon />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div>
            <h4 className="ft-col-head">System Links</h4>
            <ul className="ft-links">
              {SYSTEM_LINKS.map((link) => (
                <li key={link.label}>
                  <a href={link.href}>{link.label}</a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="ft-col-head">Our Offerings</h4>
            <ul className="ft-links">
              {OFFERING_LINKS.map((link) => (
                <li key={link.label}>
                  <a href={link.href}>{link.label}</a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="ft-col-head">Newsletter</h4>
            <p className="ft-newsletter-desc">Subscribe to receive daily cosmic forecasts, Vedic wisdom, and exclusive divine updates.</p>
            <form className="ft-input-row" onSubmit={handleSubscribe}>
              <input
                className="ft-email-input"
                type="email"
                placeholder="Your email address"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button type="submit" className="ft-subscribe-btn" disabled={subscribing}>
                {subscribing ? 'Subscribing…' : 'Subscribe'}
                <FiArrowRight size={13} />
              </button>
            </form>
          </div>
        </div>

        <div className="ft-contact-strip">
          <div className="ft-contact-item">
            <FiPhone size={15} />
            <a href={`tel:+${telDigits(phone)}`}>{displayPhone(phone)}</a>
          </div>
          <div className="ft-contact-item">
            <FiMail size={15} />
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
          </div>
          <div className="ft-contact-item">
            <FiMapPin size={15} />
            <span>{address}</span>
          </div>
        </div>

        <div className="ft-bottom" style={{ paddingTop: '24px', paddingBottom: '32px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div className="ft-payment-badges">
              {CARD_ICONS.map(({ name, Icon }) => (
                <div key={name} title={name}>
                  <Icon />
                </div>
              ))}
            </div>
            <p className="ft-copyright">© {new Date().getFullYear()} {companyName}. All rights reserved. Vedic Astrology Mission.</p>
          </div>
          <div className="ft-legal-links">
            {LEGAL_LINKS.map((link) => (
              <a href={link.href} key={link.label}>
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {toast && <div className="ft-toast">{toast}</div>}
    </footer>
  )
}
